import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import BootstrapTable from 'react-bootstrap-table-next';
import paginationFactory, {
  PaginationProvider,
  PaginationListStandalone,
  PaginationTotalStandalone,
  SizePerPageDropdownStandalone,
} from 'react-bootstrap-table2-paginator';
import { NavLink, useNavigate } from 'react-router-dom';

import { RootState } from './../../../store';
import Page from './../../../components/Layout/Page/Page';
import { IPagination, ISortingParameters } from './offers.model';
import { ISearchParameters } from '../../../components/Tools/Filters/model';
import Filters from '../../../components/Tools/Filters/Filters';

import 'react-bootstrap-table-next/dist/react-bootstrap-table2.min.css';
import 'react-bootstrap-table2-paginator/dist/react-bootstrap-table2-paginator.min.css';

import classes from './Offers.module.scss';
import { Badge, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { formatDate } from '../../../utils/formatDate';
import { TimeFormatter } from '../../../components/Tools/TableTimeCountdownFormatter/TableTimeCountdownFormatter';
import { OfferStatusDisplay } from '../../../components/OfferStatusDisplay';
import { AdStatusDisplay } from '../../../components/AdStatusDisplay';
import { client } from '../../../services/http/instance';
import dayjs from 'dayjs';

const OfferStatus = ({ cell, translate }) => {
  let offerStatus = 'default';
  switch (cell) {
    case '1':
      offerStatus = 'in_progress';
      break;
    case '2':
      offerStatus = 'auction';
      break;
    case '3':
      offerStatus = 'accepted';
      break;
    case '4':
      offerStatus = 'declined';
      break;
    case '5':
      offerStatus = 'canceled';
      break;
    case '6':
      offerStatus = 'finished';
      break;
    default:
      break;
  }
  return (
    <div className={`${classes.status} ${classes[offerStatus]}`}>
      <span></span>
      {translate[offerStatus]}
    </div>
  );
};

const CustomActionCol = props => {
  const t_global = useSelector((state: RootState) => {
    return state.translations.translations[state.translations.appLanguage]['global'];
  });

  const auction = props?.cell || false;
  return (
    <>
      <span>{auction ? t_global.yes : t_global.no}</span>
    </>
  );
};

const CustomSeeOffersCol = props => {
  const t_advertisements = useSelector((state: RootState) => {
    return state.translations.translations[state.translations.appLanguage]['advertisements'];
  });

  const totalOffers = props?.row?.total_offer_count || 0;
  const pendingOffers = props?.row?.pending_offer_count || 0;
  const id = props?.row?.id || null;

  if (totalOffers > 0) {
    return (
      <NavLink to={`/my-offers/preview/${id}`}>
        {t_advertisements.see_offers}
        {pendingOffers > 0 && <Badge bg="primary">{pendingOffers}</Badge>}
      </NavLink>
    );
  }

  return <div style={{ textAlign: 'center' }}>-</div>;
};

const Offers = () => {
  const [loadingData, setLoadingData] = useState<boolean>(false);
  const [pagination, setPagination] = useState<IPagination>({
    sizePerPage: 10,
    page: 1,
  });
  const [myOffers, setMyOffers]: any = useState<[]>([]);
  const [totalData, setTotalData] = useState<number>(0);
  const [sorting, setSorting] = useState<ISortingParameters>({
    sortField: null,
    sortOrder: undefined,
  });
  const [searchParameters, setSearchparameters] = useState<ISearchParameters>({
    searchValue: '',
    datumOd: '',
    datumDo: '',
    category_id: '',
  });
  const [activeTab, setActiveTab]: any = useState('active');

  const t = useSelector((state: RootState) => {
    return state.translations.translations[state.translations.appLanguage];
  });
  const navigate = useNavigate();

  const t_offers = t.offers;
  const t_global = t.global;
  const t_current_ads = t.current_advertisements;
  const t_advertisements = t.advertisements;

  const fetchMyOffers = async (
    page = 1,
    sizePerPage = 10,
    sortField = null,
    sortOrder = undefined,
    selectedTab = 'active',
    filter = searchParameters
  ) => {
    setMyOffers([]);
    setPagination({
      page,
      sizePerPage,
    });
    setLoadingData(true);
    try {
      const query = {
        own: 'offers',
        offers: 1,
        page: page ?? 1,
        status: ['active', 'inactive'].includes(selectedTab) ? selectedTab : undefined,
        size: sizePerPage ?? 10,
        search: filter.searchValue || undefined,
        sortBy: sortField,
        sortOrder,
        dateFrom: filter.datumOd || undefined,
        dateTo: filter.datumDo || undefined,
        industryId: filter.category_id || undefined,
      };

      const response = await client.get('/ads', { params: query });
      setMyOffers(response?.data?.data || []);
      setTotalData(response?.data?.total || 0);
      setLoadingData(false);
    } catch (error) {
      console.error(error);
      setLoadingData(false);
    }
  };

  const onSearchChangeHandler = (params: ISearchParameters) => {
    setSearchparameters(params);
    const { page, sizePerPage } = pagination;
    const { sortField, sortOrder } = sorting;
    fetchMyOffers(page, sizePerPage, sortField, sortOrder, activeTab, params);
  };

  const onTableChange = (type, { page, sizePerPage, sortField, sortOrder }) => {
    fetchMyOffers(page, sizePerPage, sortField, sortOrder, activeTab);
  };

  const onTabChange = (active: string) => {
    const { page, sizePerPage } = pagination;
    const { sortField, sortOrder } = sorting;
    setActiveTab(active);
    fetchMyOffers(page, sizePerPage, sortField, sortOrder, active);
  };

  const tableColumns = [
    {
      dataField: 'id',
      text: t_advertisements.offer_num,
      sort: true,
    },
    {
      dataField: 'created_at',
      text: t_global.creation_date,
      formatter: cell => formatDate(cell),
      sort: true,
    },
    {
      dataField: 'offer_due_date',
      text: t_global.offer_due_date,
      formatter: cell => (
        <>
          {dayjs(cell).format('DD.MM.YYYY')} <br /> {dayjs(cell).format('HH:mm')}
        </>
      ),
      sort: true,
    },
    {
      dataField: 'title',
      text: t_advertisements.offer_name,
      formatter: (cell, row) => {
        if (cell) {
          return (
            <OverlayTrigger placement="top-start" overlay={<Tooltip>{cell}</Tooltip>}>
              <span>{cell}</span>
            </OverlayTrigger>
          );
        }
      },
      sort: true,
    },
    {
      dataField: 'company.naziv',
      text: t.current_advertisements.company,
      formatter: (cell, row) => {
        if (row.company.id) {
          return row.anonymous ? (
            t_global.anonymous
          ) : (
            <OverlayTrigger placement="top-start" overlay={<Tooltip>{cell}</Tooltip>}>
              <NavLink to={`/company/${row.company.id}`} className={classes.AdLink} onClick={e => e.stopPropagation()}>
                {cell}
              </NavLink>
            </OverlayTrigger>
          );
        }
      },
      sort: true,
    },
    {
      dataField: 'industry.name',
      text: t_advertisements.categories,
      formatter: (cell, row) => {
        if (cell) {
          return (
            <OverlayTrigger placement="top-start" overlay={<Tooltip>{cell}</Tooltip>}>
              <span>{cell}</span>
            </OverlayTrigger>
          );
        }
      },
      sort: true,
    },
    {
      dataField: 'auction_start_date',
      text: t_global.start,
      formatter: (cell, row) => {
        if (!row.aukcija) return <div style={{ textAlign: 'center' }}>-</div>;

        return TimeFormatter(cell);
      },
      sort: true,
    },
    {
      dataField: 'auction_end_date',
      text: t_advertisements.closure,
      formatter: (cell, row) => {
        if (!row.aukcija) return <div style={{ textAlign: 'center' }}>-</div>;

        return TimeFormatter(cell);
      },
      sort: true,
    },
    {
      dataField: 'aukcija',
      text: t_advertisements.auction,
      formatter: (cell, row) => <CustomActionCol row={row} cell={cell} />,
      sort: true,
    },
    {
      dataField: 'status',
      text: t_global.status,
      formatter: cell => <AdStatusDisplay status={cell} />,
      sort: true,
    },
    {
      dataField: 'action',
      formatter: (cell, row) => <CustomSeeOffersCol cell={cell} row={row} />,
    },
  ];

  const customCaret = (order, column) => {
    if (!order) {
      return (
        <span style={{ display: 'inline-block', position: 'relative', top: '-5px' }}>
          <span className={classes.sort_wrapper}>
            <span className={`${classes.caret} ${classes.caret_up}`}></span>
            <span className={classes.caret}></span>
          </span>
        </span>
      );
    } else if (order === 'asc') {
      return (
        <span style={{ display: 'inline-block', position: 'relative', top: '-2px' }}>
          <span className={classes.sort_wrapper}>
            <span className={`${classes.caret} ${classes.caret_up}`}></span>
          </span>
        </span>
      );
    } else if (order === 'desc') {
      return (
        <span style={{ display: 'inline-block', position: 'relative', top: '-2px' }}>
          <span className={classes.sort_wrapper}>
            <span className={classes.caret}></span>
          </span>
        </span>
      );
    } else {
      return null;
    }
  };

  const customTotal = (from, to, size) => (
    <span className="react-bootstrap-table-pagination-total">
      {`${t_global['table_total_text']} ${from} ${t_global['table_total_text_1']} ${to} ${t_global['table_total_text_2']} ${size} ${t_global['table_total_text_3']}`}
    </span>
  );

  const paginationOptions = {
    custom: true,
    totalSize: totalData,
    paginationSize: 4,
    pageStartIndex: 1,
    alwaysShowAllBtns: true,
    withFirstAndLast: false,
    hideSizePerPage: false,
    hidePageListOnlyOnePage: false,
    firstPageText: '<<',
    prePageText: '<',
    nextPageText: '>',
    lastPageText: '>>',
    nextPageTitle: 'First page',
    prePageTitle: 'Pre page',
    firstPageTitle: 'Next page',
    lastPageTitle: 'Last page',
    showTotal: true,
    paginationTotalRenderer: customTotal,
    disablePageTitle: true,
    sizePerPageList: [
      {
        text: '10',
        value: 10,
      },
      {
        text: '50',
        value: 50,
      },
      {
        text: '100',
        value: 100,
      },
    ],
  };

  return (
    <React.Fragment>
      <Page table>
        <div className={classes.offers_screen_wrapper}>
          <Filters
            industryView="offers"
            onSearchChange={onSearchChangeHandler}
            advertisements={myOffers}
            title={t.offers.my_offers}
          />
          <div className={classes.table_wrapper}>
            <div className={classes.buttons_wrapper}>
              <div
                className={`${classes.button} ${activeTab === 'active' ? classes.active : ''}`}
                onClick={() => onTabChange('active')}
              >
                {t_current_ads['active']}{' '}
                {activeTab === 'active' ? (
                  <Badge bg="primary">{activeTab === 'active' ? totalData : ''}</Badge>
                ) : (
                  <span className={classes.text}>{activeTab === 'active' ? totalData : ''}</span>
                )}
              </div>
              <div
                className={`${classes.button} ${activeTab === 'inactive' ? classes.active : ''}`}
                onClick={() => onTabChange('inactive')}
              >
                {t_current_ads['finished']}{' '}
                {activeTab === 'inactive' ? (
                  <Badge bg="primary">{activeTab === 'inactive' ? totalData : ''}</Badge>
                ) : (
                  <span className={classes.text}>{activeTab === 'inactive' ? totalData : ''}</span>
                )}
              </div>
            </div>
            <div className={classes.table_container}>
              <PaginationProvider pagination={paginationFactory(paginationOptions)}>
                {({ paginationProps, paginationTableProps }) => (
                  <div className={classes.table_wrapper_inner}>
                    <BootstrapTable
                      hover
                      keyField="id"
                      remote={true}
                      onTableChange={onTableChange}
                      data={myOffers}
                      columns={tableColumns}
                      classes={classes.offers_table}
                      noDataIndication={loadingData ? t_global.loading : t_global.no_data}
                      sort={{
                        sortCaret: customCaret,
                      }}
                      rowEvents={{
                        onClick: (e, row) => {
                          navigate(`/my-offers/preview/${row.id}`);
                        },
                      }}
                      {...paginationTableProps}
                    />
                    <div className={classes.pagination_wrapper}>
                      <PaginationTotalStandalone {...paginationProps} />
                      <div>
                        <PaginationListStandalone {...paginationProps} />
                        <SizePerPageDropdownStandalone {...paginationProps} />
                      </div>
                    </div>
                  </div>
                )}
              </PaginationProvider>
            </div>
          </div>
        </div>
      </Page>
    </React.Fragment>
  );
};

export default Offers;
